// components/common/charts/chartUtils.js - Shared utilities for all chart components
import React from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

// ✅ SHARED COLOR CONFIGURATION
export const CHART_COLORS = {
  // Primary colors
  primary: "#009391",        // Teal
  secondary: "#0093CF",      // Blue
  tertiary: "#152451",       // Navy
  
  // Revenue-specific
  retainer: "#009391",       // Teal
  project: "#0093CF",        // Blue
  other: "#152451",          // Navy
  
  // Comparison & projected
  lastYear: "#DCDEEF",       // Light gray
  projected: "#E9D5FF",      // Light violet
  actual: "#10B981",         // Green
  
  // Chart elements
  gridLines: "#E5E7EB",      // Light gray
  axisText: "#6B7280",       // Medium gray
  tooltipBorder: "#ccc",     // Light gray
  
  // Status colors
  positive: "#10B981",       // Green
  negative: "#EF4444",       // Red
  neutral: "#6B7280",        // Gray
  
  // Growth indicators
  growthPositive: "#059669", // Dark green
  growthNegative: "#dc2626", // Red
};

// ✅ COMPACT NUMBER FORMATTER
export const formatCompactNumber = (value) => {
  if (value === 0) return "0";
  
  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  
  if (absValue >= 1000000) {
    return `${sign}${(absValue / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    return `${sign}${(absValue / 1000).toFixed(1)}K`;
  }
  
  return `${sign}${absValue.toFixed(0)}`;
};

// ✅ CURRENCY FORMATTER
export const formatChartCurrency = (value) => {
  if (value === 0) return "$0";
  return `$${formatCompactNumber(value)}`;
};

// ✅ MOBILE LEGEND COMPONENT
export const MobileLegend = ({ legendItems, showLegend, onToggle }) => {
  return (
    <div className="sm:hidden relative">
      {/* Toggle Button */}
      <button
        onClick={onToggle}
        onTouchStart={onToggle}
        className="absolute top-2 right-2 z-10 bg-gray-800 bg-opacity-75 text-white px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 shadow-lg"
      >
        <InformationCircleIcon className="w-4 h-4" />
        Legend
      </button>

      {/* Legend Popup */}
      {showLegend && (
        <div className="absolute top-12 right-2 bg-white shadow-2xl rounded-lg p-3 border border-gray-200 min-w-[180px] z-20">
          <div className="space-y-2">
            {legendItems.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                {item.type === "bar" ? (
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: item.color }}
                  />
                ) : item.type === "line" ? (
                  <svg width="24" height="14" style={{ display: 'block' }}>
                    <line
                      x1="0"
                      y1="7"
                      x2="24"
                      y2="7"
                      stroke={item.color}
                      strokeWidth="2"
                    />
                    <rect
                      x="9"
                      y="4"
                      width="6"
                      height="6"
                      fill={item.color}
                      stroke={item.color}
                      strokeWidth="2"
                    />
                  </svg>
                ) : null}
                <span className="text-xs text-gray-700">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ✅ CUSTOM TOOLTIP COMPONENT
export const CustomChartTooltip = ({ 
  active, 
  payload, 
  label, 
  valueFormatter = (v) => formatChartCurrency(v),
  showComparison = false,
  comparisonLabel = "Last Year",
  showGrowth = false,
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  // Separate main values from comparison
  const mainItems = showComparison 
    ? payload.filter(p => !p.dataKey.includes("last_year") && !p.dataKey.includes("comparison"))
    : payload;
  
  const comparisonItem = showComparison 
    ? payload.find(p => p.dataKey.includes("last_year") || p.dataKey.includes("comparison"))
    : null;

  // Calculate total
  const currentTotal = mainItems.reduce((sum, item) => sum + (item.value || 0), 0);

  // Don't show tooltip if no data
  if (currentTotal === 0 && (!comparisonItem || comparisonItem.value === 0)) {
    return null;
  }

  return (
    <div
      style={{
        padding: "12px",
        background: "white",
        border: `1px solid ${CHART_COLORS.tooltipBorder}`,
        borderRadius: "5px",
        fontSize: "14px",
      }}
    >
      <div style={{ fontWeight: "bold", marginBottom: "8px" }}>{label}</div>

      {/* Main values */}
      {currentTotal > 0 && (
        <div style={{ marginBottom: "8px" }}>
          {mainItems.map((item, index) => (
            item.value > 0 && (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "16px",
                  marginBottom: "4px",
                }}
              >
                <span style={{ color: item.color, fontWeight: "500" }}>
                  {item.name}:
                </span>
                <span style={{ fontWeight: "600" }}>
                  {valueFormatter(item.value)}
                </span>
              </div>
            )
          ))}
        </div>
      )}

      {/* Total */}
      {currentTotal > 0 && mainItems.length > 1 && (
        <div
          style={{
            borderTop: "1px solid #e5e7eb",
            paddingTop: "6px",
            display: "flex",
            justifyContent: "space-between",
            gap: "16px",
            fontWeight: "bold",
          }}
        >
          <span>Total:</span>
          <span>{valueFormatter(currentTotal)}</span>
        </div>
      )}

      {/* Comparison section */}
      {comparisonItem && comparisonItem.value > 0 && (
        <div
          style={{
            borderTop: currentTotal > 0 ? "1px solid #e5e7eb" : "none",
            marginTop: currentTotal > 0 ? "8px" : "0",
            paddingTop: currentTotal > 0 ? "6px" : "0",
            fontSize: "13px",
            color: CHART_COLORS.axisText,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              marginBottom: "4px",
            }}
          >
            <span>{comparisonLabel}:</span>
            <span style={{ fontWeight: "500" }}>
              {valueFormatter(comparisonItem.value)}
            </span>
          </div>
          {showGrowth && currentTotal > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "16px",
              }}
            >
              <span>YoY Growth:</span>
              <span
                style={{
                  fontWeight: "600",
                  color:
                    currentTotal > comparisonItem.value 
                      ? CHART_COLORS.growthPositive 
                      : CHART_COLORS.growthNegative,
                }}
              >
                {(
                  ((currentTotal - comparisonItem.value) / comparisonItem.value) *
                  100
                ).toFixed(1)}
                %
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ✅ CUSTOM DOT COMPONENT FOR LINES
export const CustomChartDot = (props) => {
  const { cx, cy, payload, dataKey } = props;

  const value = payload[dataKey];
  if (!value || value === 0) {
    return null;
  }

  return (
    <rect
      x={cx - 3}
      y={cy - 3}
      width={6}
      height={6}
      fill={CHART_COLORS.lastYear}
      stroke={CHART_COLORS.lastYear}
      strokeWidth={2}
    />
  );
};

// ✅ RESPONSIVE CHART CONFIG
export const getResponsiveChartConfig = () => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  
  return {
    margin: {
      top: 20,
      right: 10,
      left: 0,
      bottom: isMobile ? 60 : 70,
    },
    barCategoryGap: isMobile ? "8%" : "15%",
    xAxisConfig: {
      fontSize: isMobile ? 10 : 11,
      angle: -45,
      textAnchor: "end",
      height: 80,
    },
    yAxisConfig: {
      fontSize: isMobile ? 10 : 11,
      width: 45,
    },
  };
};

// ✅ DESKTOP LEGEND COMPONENT
export const DesktopLegend = ({ legendItems }) => {
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      gap: '20px',
      paddingTop: '20px',
      fontSize: '13px',
      flexWrap: 'wrap'
    }}>
      {legendItems.map((item, index) => {
        const isLine = item.type === "line";
        return (
          <div 
            key={`legend-${index}`} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px' 
            }}
          >
            {/* Custom icon */}
            <svg width="20" height="12" style={{ display: 'block' }}>
              {isLine ? (
                <>
                  {/* Line */}
                  <line
                    x1="0"
                    y1="6"
                    x2="20"
                    y2="6"
                    stroke={item.color}
                    strokeWidth="2"
                  />
                  {/* Square dot in the middle */}
                  <rect
                    x="7"
                    y="3"
                    width="6"
                    height="6"
                    fill={item.color}
                    stroke={item.color}
                    strokeWidth="2"
                  />
                </>
              ) : (
                /* Rectangle for bars */
                <rect
                  x="3"
                  y="0"
                  width="14"
                  height="12"
                  fill={item.color}
                />
              )}
            </svg>
            {/* Label */}
            <span style={{ color: '#000000', fontSize: '13px' }}>{item.label}</span>
          </div>
        );
      })}
    </div>
  );
};